import React, { useContext, useEffect, useRef, useState } from 'react';
import { GlobalContext } from '../../contexts/GlobalContext';

const LeetCodeProblemModal = ({ isOpen, onClose, onSubmit }) => {
  const { questions, fetchQuestions } = useContext(GlobalContext);
  const [problemName, setProblemName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [customProblem, setCustomProblem] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch questions when component mounts if they're not already loaded
  useEffect(() => {
    if (questions.length === 0) {
      fetchQuestions();
    }
  }, [questions.length, fetchQuestions]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter questions based on search query
  const filteredQuestions = questions.filter((q) =>
    q.question.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(customProblem ? problemName : searchQuery);
    onClose();
  };

  const handleQuestionSelect = (question) => {
    setProblemName(question.question);
    setSearchQuery(question.question);
    setShowDropdown(false);
    setCustomProblem(false);
  };

  const handleCustomProblem = () => {
    setCustomProblem(true);
    setShowDropdown(false);
    setProblemName(searchQuery); // Keep the current search as the initial custom problem name
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    if (customProblem) {
      setProblemName(value);
    } else {
      setSearchQuery(value);
      setShowDropdown(value.length > 0);
    }
  };

  const handleBackToSearch = () => {
    setCustomProblem(false);
    setSearchQuery(problemName); // Keep the custom problem name as the search query
    setShowDropdown(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-lg mx-4">
        <h2 className="text-xl font-semibold mb-4">Which Leetcode problem did you solve?</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            {customProblem ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <input
                    ref={inputRef}
                    type="text"
                    value={problemName}
                    onChange={handleInputChange}
                    placeholder="Enter custom problem name..."
                    className="w-full px-3 py-2 border border-blue-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <button
                  type="button"
                  onClick={handleBackToSearch}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  ← Back to search
                </button>
              </div>
            ) : (
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={handleInputChange}
                placeholder="Search for a problem..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500"
                required
              />
            )}
            {showDropdown && !customProblem && searchQuery.length > 0 && (
              <div
                ref={dropdownRef}
                className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
              >
                {filteredQuestions.length > 0 ? (
                  <>
                    {filteredQuestions.map((question, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleQuestionSelect(question)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100"
                      >
                        <div className="flex justify-between items-center">
                          <span>{question.question}</span>
                          <span
                            className={`text-xs px-2 py-1 rounded-sm ${
                              question.difficulty === 'Easy'
                                ? 'bg-green-100 text-green-800'
                                : question.difficulty === 'Medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {question.difficulty}
                          </span>
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={handleCustomProblem}
                      className="w-full px-4 py-2 text-left text-blue-600 hover:bg-gray-100 focus:outline-hidden focus:bg-gray-100 border-t border-gray-200"
                    >
                      + I don't see my problem (create custom)
                    </button>
                  </>
                ) : (
                  <div className="px-4 py-2 text-gray-500">
                    No problems found.{' '}
                    <button
                      type="button"
                      onClick={handleCustomProblem}
                      className="text-blue-600 hover:underline"
                    >
                      Add custom problem
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeetCodeProblemModal;
